import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";

const sanitizeFilename = (s) => (s || "").toString().trim().replace(/[^\w.\-]+/g, "-");

/**
 * Get-or-create the per-case "Orders" folder so re-exports don't pile up
 * duplicate folders. Match is keyed on caseId + folder name + lawyer_uid.
 */
const ensureOrdersFolder = async ({ caseId, caseNo, userUid }) => {
  const snap = await getDocs(
    query(
      collection(db, "folders"),
      where("caseId", "==", caseId),
      where("name", "==", "Orders"),
      where("lawyer_uid", "==", userUid)
    )
  );
  if (!snap.empty) return snap.docs[0].id;

  const created = await addDoc(collection(db, "folders"), {
    name: "Orders",
    caseId,
    caseNo: caseNo || "",
    lawyer_uid: userUid,
    createdAt: new Date().toISOString(),
  });
  return created.id;
};

/**
 * Register every Supreme Court judgement/order PDF as a file under the case's
 * "Orders" folder.
 *
 * Why URLs and not blob uploads: the upstream `api.sci.gov.in` PDFs are served
 * without CORS, so the browser can't fetch them as blobs to re-upload to
 * Firebase Storage. We store the original PDF URL as `file_url` — `CaseFiles.jsx`
 * already routes any non-firebase URL through its built-in PDF proxy, so
 * viewing/downloading from inside the portal works transparently.
 *
 * - File name: `{orderDate}_orders.pdf` (sanitised; `_2`, `_3` suffix if two
 *   orders share the same date).
 * - Silent on individual failures — counted, never thrown.
 *
 * @param {Object} args
 * @param {Object} args.user           Firebase auth user
 * @param {string} args.caseId         Firestore doc id of the exported case
 * @param {string} args.caseNo
 * @param {string} args.caseCollection "pending" | "disposed" | "decided"
 * @param {Array}  args.judgementOrders Normalized DTO judgementOrders array
 * @returns {Promise<{uploaded:number, failed:number, total:number}>}
 */
export const uploadSciOrderPdfs = async ({
  user,
  caseId,
  caseNo,
  caseCollection,
  judgementOrders,
}) => {
  if (!user || !caseId || !Array.isArray(judgementOrders)) {
    return { uploaded: 0, failed: 0, total: 0 };
  }

  // Each order carries the PDF link under `url` (alias `documentUrl`).
  const entries = judgementOrders
    .map((o) => ({
      url: o?.url || o?.documentUrl || "",
      date: o?.date || "",
    }))
    .filter((o) => o.url);
  if (entries.length === 0) return { uploaded: 0, failed: 0, total: 0 };

  let folderId;
  try {
    folderId = await ensureOrdersFolder({ caseId, caseNo, userUid: user.uid });
  } catch (err) {
    console.error("sciFileSync: ensureOrdersFolder failed:", err);
    return { uploaded: 0, failed: entries.length, total: entries.length };
  }

  // Disambiguate filenames when two orders share the same date.
  const filenameSeen = new Map();
  const buildName = (o, idx) => {
    const datePart = sanitizeFilename(o.date) || `order-${idx + 1}`;
    const base = `${datePart}_orders.pdf`;
    const count = (filenameSeen.get(base) || 0) + 1;
    filenameSeen.set(base, count);
    return count === 1 ? base : `${datePart}_orders_${count}.pdf`;
  };

  const results = await Promise.all(
    entries.map(async (o, idx) => {
      const fileName = buildName(o, idx);
      try {
        await addDoc(collection(db, "files"), {
          caseNo: caseNo || "",
          caseId,
          fileName,
          file_url: o.url,
          lawyer_uid: user.uid,
          uploadedAt: new Date().toISOString(),
          folderId,
          source: "sci-judgement-order",
        });
        return { ok: true, url: o.url };
      } catch (err) {
        console.error("sciFileSync: failed to register PDF:", err);
        return { ok: false };
      }
    })
  );

  const newUrls = results.filter((r) => r.ok).map((r) => r.url);
  const uploaded = newUrls.length;
  const failed = results.length - uploaded;

  if (newUrls.length > 0 && caseCollection) {
    try {
      const caseRef = doc(db, caseCollection, caseId);
      const snap = await getDoc(caseRef);
      if (snap.exists()) {
        await updateDoc(caseRef, { files: arrayUnion(...newUrls) });
      }
    } catch (err) {
      console.error("sciFileSync: failed to update case files[]:", err);
    }
  }

  return { uploaded, failed, total: entries.length };
};
